import React, { useState, useEffect } from 'react';
import { Card, Table, Tag, Button, Drawer, Timeline, Statistic, Row, Col, Avatar, Descriptions, Divider } from 'antd';
import { UserOutlined, DollarCircleOutlined, MessageOutlined, LoginOutlined, CheckCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import api from '../api/client';

export default function JourneyDetail() {
  const { id } = useParams();
  const [journey, setJourney] = useState(null);
  const [events, setEvents] = useState([]);
  const [payments, setPayments] = useState([]);
  const [drawerVisible, setDrawerVisible] = useState(false);

  useEffect(() => {
    fetchJourneyData();
  }, [id]);

  const fetchJourneyData = async () => {
    try {
      const response = await api.get(`/analytics/journey/${id}`);
      setJourney(response.data.journey);
      setEvents(response.data.events);
      setPayments(response.data.payments);
    } catch (err) {
      console.error(err);
    }
  };

  if (!journey) {
    return <div>Loading...</div>;
  }

  const totalRevenue = payments.reduce((sum, p) => sum + (p.amount || 0), 0);
  
  // Parse custom attributes safely
  let userProfile = {};
  try {
    userProfile = journey.custom_attributes || {};
    if (typeof userProfile === 'string') {
        userProfile = JSON.parse(userProfile);
    }
  } catch (e) {
    console.error("Error parsing attributes", e);
  }

  const getEventIcon = (type) => {
    switch (type) {
      case 'payment':
      case 'payment_completed':
        return <DollarCircleOutlined style={{ fontSize: '16px', color: '#10b981' }} />;
      case 'subscription':
      case 'channel_join':
      case 'bot_join':
        return <LoginOutlined style={{ fontSize: '16px', color: '#3b82f6' }} />;
      case 'message':
        return <MessageOutlined style={{ fontSize: '16px', color: '#8b5cf6' }} />;
      default:
        return <ClockCircleOutlined style={{ fontSize: '16px', color: '#6b7280' }} />;
    }
  };

  const getEventColor = (type) => {
     switch (type) {
      case 'payment':
      case 'payment_completed': return 'green';
      case 'subscription': 
      case 'channel_join': return 'blue';
      default: return 'gray';
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24 }}>
        <Avatar size={64} icon={<UserOutlined />} src={userProfile.photo_url} style={{ backgroundColor: '#10b981', marginRight: 16 }} />
        <div>
          <h1 style={{ marginBottom: 0 }}>
            {userProfile.first_name ? `${userProfile.first_name} ${userProfile.last_name || ''}` : `User #${journey.id}`}
          </h1>
          <div style={{ color: '#6b7280' }}>
            {userProfile.username ? `@${userProfile.username}` : (userProfile.telegram_username ? `@${userProfile.telegram_username}` : 'Anonymous User')}
            {journey.subscription_status === 'subscribed' && <Tag color="green" style={{ marginLeft: 8 }}>Subscribed</Tag>}
          </div>
        </div>
      </div>
      
      <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
        <Col xs={24} md={16}>
             <Card title="User Profile & Attributes" bordered={false} className="shadow-sm">
                <Descriptions column={2}>
                    <Descriptions.Item label="Source">{journey.source}</Descriptions.Item>
                    <Descriptions.Item label="Source ID">{journey.source_id}</Descriptions.Item>
                    <Descriptions.Item label="First Seen">{new Date(journey.first_seen).toLocaleString()}</Descriptions.Item>
                    <Descriptions.Item label="Last Action">{journey.last_action ? new Date(journey.last_action).toLocaleString() : '-'}</Descriptions.Item>
                    <Descriptions.Item label="Telegram ID">{userProfile.telegram_id || userProfile.id || '-'}</Descriptions.Item>
                    <Descriptions.Item label="Language">{userProfile.language_code || '-'}</Descriptions.Item>
                </Descriptions>
                
                {journey.invite_link_used && (
                    <div style={{ marginTop: 16, padding: 12, background: '#f0fdf4', borderRadius: 6, border: '1px solid #bbf7d0' }}>
                        <span style={{ color: '#15803d', fontWeight: 500 }}>🎯 Joined via Invite Link: </span>
                        <code style={{ color: '#15803d' }}>{journey.invite_link_used}</code>
                    </div>
                )}
             </Card>
        </Col>
        <Col xs={24} md={8}>
            <Row gutter={[0, 16]}>
                <Col span={24}>
                    <Card bordered={false} className="shadow-sm" style={{ textAlign: 'center' }}>
                        <Statistic
                            title="Total Revenue (LTV)"
                            value={totalRevenue}
                            precision={2}
                            prefix={<DollarCircleOutlined />}
                            suffix="₽"
                            valueStyle={{ color: '#059669', fontWeight: 'bold' }}
                        />
                    </Card>
                </Col>
                <Col span={24}>
                     <Card bordered={false} className="shadow-sm" style={{ textAlign: 'center' }}>
                        <Statistic
                            title="Total Interactions"
                            value={events.length}
                            prefix={<MessageOutlined />}
                            valueStyle={{ color: '#3b82f6' }}
                        />
                    </Card>
                </Col>
            </Row>
        </Col>
      </Row>

      <Row gutter={[24, 24]}>
        <Col xs={24} lg={12}>
          <Card title="Journey Timeline" style={{ height: '100%' }}>
            <Timeline mode="left">
              {events.map((event, idx) => (
                <Timeline.Item 
                    key={idx} 
                    dot={getEventIcon(event.event_type)}
                    color={getEventColor(event.event_type)}
                >
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontWeight: 600, fontSize: '14px' }}>
                        {event.event_type.replace(/_/g, ' ').toUpperCase()}
                    </span>
                    <span style={{ color: '#9ca3af', fontSize: '12px' }}>
                        {new Date(event.created_at).toLocaleString()}
                    </span>
                    {event.event_data && (
                        <div style={{ fontSize: '12px', color: '#4b5563', marginTop: 4, background: '#f3f4f6', padding: '4px 8px', borderRadius: 4 }}>
                            {JSON.stringify(event.event_data).slice(0, 100)}
                        </div>
                    )}
                  </div>
                </Timeline.Item>
              ))}
              <Timeline.Item dot={<LoginOutlined />} color="green">
                  <span style={{ fontWeight: 600 }}>First Seen</span>
                  <br />
                  <span style={{ color: '#9ca3af', fontSize: '12px' }}>{new Date(journey.first_seen).toLocaleString()}</span>
              </Timeline.Item>
            </Timeline>
          </Card>
        </Col>
        
        <Col xs={24} lg={12}>
          <Card title="Payment History" style={{ height: '100%' }}>
            <Table
              columns={[
                { title: 'Amount', dataIndex: 'amount', render: v => <span style={{ fontWeight: 'bold', color: '#059669' }}>{v} ₽</span> },
                { title: 'Status', dataIndex: 'status', render: status => <Tag color={status === 'completed' ? 'success' : 'warning'}>{status.toUpperCase()}</Tag> },
                { title: 'Date', dataIndex: 'created_at', render: date => new Date(date).toLocaleDateString() },
              ]}
              dataSource={payments}
              pagination={false}
              rowKey="id"
            />
             {payments.length === 0 && (
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#9ca3af' }}>
                    <DollarCircleOutlined style={{ fontSize: 32, marginBottom: 8 }} />
                    <p>No payments recorded yet</p>
                </div>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
}
